import { EventEmitter } from "events";
import { isEmpty, pickBy, includes, first, keys, omit } from 'lodash';
import Dispatcher from '../dispatcher/appDispatcher';
import ActionType from '../constants/actionType';
import { Notification } from 'component/notification/Notification';
import { getNotification } from '../component/notification/getNotification';

/**
 * @typedef {Object} NotificationMetaData
 * @property {string} type
 * @property {string} message
 * @property {NotificationMetaDataExtraData} additionalViewData
 * @property {*}
 */

/** @typedef {Object} NotificationMetaDataExtraData
 * @mixes NotificationViewData
 * @property {string} [linkUrl]
 * @property {string} [linkMessage]
 */

/**
 * @typedef {Object} NotificationViewData
 * @mixin
 * @property {ReactElement} content
 * @property {string} [key]
 * @property {boolean} [closable]
 * @property {number} [duration]
 * @property {Function} [onClose]
 * @property {Object} [style]
 * @property {ReactNode} {closeIcon}
 */

/**
 * @callback GetNotificationViewData
 * @param {NotificationMetaData} notificationData
 * @param {Function} t
 * @returns {NotificationViewData}
 */

export class NotificationStore extends EventEmitter {

  /**
   * @type {NotificationStore | null}
   * @private
   */
  static instance = null;

  /** @return {NotificationStore} */
  static getInstance() {
    if (!NotificationStore.instance) {
      NotificationStore.instance = new NotificationStore();
    }
    return NotificationStore.instance;
  }

  static destroy() {
    NotificationStore.getInstance().destroy();
    NotificationStore.instance = null;
  }

  static SUCCESS_NOTIFICATION = 'SUCCESS_NOTIFICATION';
  static ERROR_NOTIFICATION = 'ERROR_NOTIFICATION';
  static INFO_NOTIFICATION = 'INFO_NOTIFICATION';
  static WARNING_NOTIFICATION = 'WARNING_NOTIFICATION';

  static CHANGE_EVENT = 'NOTIFICATION_CHANGE_EVENT';

  get notificationViewData() { return this.notificationData; }

  /** @protected */
  get _notificationToActionTypeMap() {
    return {
      [NotificationStore.SUCCESS_NOTIFICATION]: [ActionType.SHOW_SUCCESS_NOTIFICATION],
      [NotificationStore.ERROR_NOTIFICATION]: [ActionType.SHOW_ERROR_NOTIFICATION],
      [NotificationStore.INFO_NOTIFICATION]: [ActionType.SHOW_INFO_NOTIFICATION],
      [NotificationStore.WARNING_NOTIFICATION]: [ActionType.SHOW_WARNING_NOTIFICATION]
    };
  }

  /** @type {Object<string, GetNotificationViewData>} */
  get notificationData() { return this._notificationData; }
  _notificationData = {
    [NotificationStore.SUCCESS_NOTIFICATION]: getNotification(Notification.TYPES.TYPE_SUCCESS),
    [NotificationStore.ERROR_NOTIFICATION]: getNotification(Notification.TYPES.TYPE_ERROR),
    [NotificationStore.INFO_NOTIFICATION]: getNotification(Notification.TYPES.TYPE_MESSAGE),
    [NotificationStore.WARNING_NOTIFICATION]: getNotification(Notification.TYPES.TYPE_WARNING)
  };

  /** @type {Array<NotificationMetaData>} */
  _notificationsMetaData = [];

  _dispatchToken;

  constructor() {
    super();
    this._dispatchToken = Dispatcher.register(this._processNotificationMetaData);
  }

  destroy() {
    this.removeAllListeners(NotificationStore.CHANGE_EVENT);
    Dispatcher.unregister(this._dispatchToken);
  }

  /**
   * @return {NotificationMetaData}
   */
  popNotification() { return this._notificationsMetaData.pop(); }

  /**
   * @param {string} type
   * @param {GetNotificationViewData} cb
   */
  registerNotificationViewDataGetter = (type, cb) => {
    this._notificationData[type] = cb;
  }
  getNotificationViewDataGetter = (type) => this.notificationData[type];

  addChangeListener = (callback) => { this.on(NotificationStore.CHANGE_EVENT, callback); }
  removeChangeListener = (callback) => { this.removeListener(NotificationStore.CHANGE_EVENT, callback); }
  emitChange = () => { this.emit(NotificationStore.CHANGE_EVENT); }

  _processNotificationMetaData = (action) => {
    const notificationData = this._reduce(action);
    if (notificationData) {
      this._pushNotification(notificationData);
      this.emitChange();
    }
  }

  /**
   * @param action
   * @return {NotificationMetaData | undefined | null}
   */
  _reduce(action) {
    const map = pickBy(this._notificationToActionTypeMap, (actionTypes) => includes(actionTypes, action.actionType));
    if (isEmpty(map)) {
      return;
    }

    const notificationType = first(keys(map));
    return { ...omit(action, 'actionType'), type: notificationType };
  }

  /**
   * @param {NotificationMetaData} notification
   */
  _pushNotification(notification) { this._notificationsMetaData.push(notification); }

}
